import { useState, useEffect, useRef, useId } from 'react';
import { api } from '../../api/client';
import { B } from '../../constants/brand';
import { naicsCodeError, normalizeNaicsCode } from '@harvus/shared';

// The NAICS field: a TYPEAHEAD over the full published table, not a free-text box.
//
// The point of holding all 2,129 NAICS 2022 codes is that nobody has to know
// one. Type "poultry" and get the industries; type "5415" and get the codes
// under it. Digits search codes and words search titles — a user typing "5415"
// wants codes and one typing "poultry" wants titles, and guessing wrong makes
// the picker feel broken.
//
// A typed value that is not a published code is refused HERE, by the same rule
// the API applies (shared/src/naics.ts checks the shape; the server checks it
// against the table and names the nearest real code). The field stays optional:
// the usual way a deal gets a code is that the classifier derives one from its
// documents, and this is the override.

const DEBOUNCE_MS = 200;

export const NaicsField = ({ value, description, onChange, label = 'NAICS Code', hint }) => {
  const [term, setTerm]       = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [chosen, setChosen]   = useState(null);   // the row behind `value`, once resolved
  const [searchErr, setSearchErr] = useState(null);
  const boxId = useId();
  const blurTimer = useRef(null);

  const shapeError = naicsCodeError(value);

  // Resolve whatever code we were handed (an existing deal's saved code) so the
  // field can show its title rather than a bare number.
  useEffect(() => {
    let cancelled = false;
    if (!value || shapeError) { setChosen(null); return undefined; }
    if (chosen && chosen.code === value) return undefined;
    api.get(`naics/code/${value}`)
      .then(r => { if (!cancelled) setChosen(r); })
      // A code from a retired vintage resolves to nothing; the description
      // stored on the deal is then all there is, and that is what it falls back
      // to below rather than showing an error.
      .catch(() => { if (!cancelled) setChosen(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, shapeError]);

  // Debounced search. Fires on the TERM, never on `value` — searching for the
  // code we just selected would reopen the list under the user's cursor.
  useEffect(() => {
    const q = term.trim();
    if (!q) { setResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get(`naics/search?q=${encodeURIComponent(q)}&limit=25`)
        .then(r => { if (!cancelled) { setResults(r?.results || []); setSearchErr(null); setOpen(true); } })
        // NOT swallowed. Silently showing an empty list made a broken lookup
        // indistinguishable from a term that matches nothing, which is exactly
        // how a routing fault reached a user as "the catalog is empty".
        .catch((e) => {
          if (cancelled) return;
          setResults([]);
          setSearchErr(e?.message || 'Could not reach the NAICS catalog.');
        });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [term]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const pick = (row) => {
    setChosen(row);
    setTerm('');
    setResults([]);
    setOpen(false);
    onChange(row.code, row.title);
  };

  // TYPING A CODE IS ENOUGH — picking from the list is a convenience, not the
  // only way in. Before this, `value` was set ONLY by pick(), so someone who
  // typed "531110" had a field that looked filled in and a Save button that
  // stayed grey; and if the catalog could not be reached, the control was
  // unusable rather than degraded. The typed digits are validated by the shared
  // rule here and against the published table on the server, which refuses an
  // unpublished code BY NAME — so nothing unchecked gets through either way.
  const typedCode = normalizeNaicsCode(term);
  const commitTyped = () => {
    if (!typedCode) return;
    setTerm(''); setResults([]); setOpen(false); setSearchErr(null);
    // No title: the server stores the official one for the code regardless of
    // what the client sends, so guessing one here would be noise.
    onChange(typedCode, '');
  };

  const clear = () => {
    setChosen(null); setTerm(''); setResults([]); setOpen(false); setSearchErr(null);
    onChange('', '');
  };

  // A code already chosen shows as a settled row with a Change control, rather
  // than as text in a box that invites half-editing it into something invalid.
  if (value && !shapeError) {
    const title = chosen?.title || description || null;
    return (
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: B.textMd, marginBottom: 6 }}>{label}</div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
          border: `1px solid ${B.border}`, borderRadius: 8, background: B.bgAlt || 'transparent',
        }}>
          <span style={{ fontWeight: 600 }}>{value}</span>
          <span style={{ color: B.textMd, fontSize: 13 }}>
            {title || <em style={{ color: B.textLt }}>not in the current NAICS edition</em>}
          </span>
          {chosen?.levelName && (
            <span style={{ fontSize: 11, color: B.textLt, border: `1px solid ${B.border}`, borderRadius: 10, padding: '1px 6px' }}>
              {chosen.levelName}
            </span>
          )}
          <button type="button" onClick={clear}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: B.navy, cursor: 'pointer', fontSize: 12 }}>
            Change
          </button>
        </div>
        {chosen?.ancestry?.length > 1 && (
          <div style={{ fontSize: 11, color: B.textLt, marginTop: 5 }}>
            {chosen.ancestry.slice(0, -1).map(a => a.title).join(' › ')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 18, position: 'relative' }}>
      <label htmlFor={boxId} style={{ display: 'block', fontSize: 12, fontWeight: 600, color: B.textMd, marginBottom: 6 }}>
        {label}
      </label>
      <input
        id={boxId}
        value={term}
        onChange={e => setTerm(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        // Enter takes the top result if the list is open, else a typed code.
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          if (open && results.length) pick(results[0]);
          else if (typedCode) commitTyped();
        }}
        // Deferred so a click on a result lands before the list unmounts.
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
        placeholder="Search an industry or a code — e.g. “poultry” or “5415”"
        style={{
          width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 14,
          border: `1px solid ${shapeError ? B.red : B.border}`, boxSizing: 'border-box',
        }}
      />
      {shapeError && <div style={{ fontSize: 12, color: B.red, marginTop: 4 }}>{shapeError}</div>}
      {!shapeError && searchErr && (
        <div style={{ fontSize: 12, color: B.red, marginTop: 4 }}>
          {searchErr}
          {typedCode && ' — you can still use the code you typed.'}
        </div>
      )}
      {/* A typed code is offered explicitly, so it is obvious that picking from
          the list is not the only way to set one — and so it still works when
          the list cannot be reached. */}
      {!shapeError && typedCode && (
        <button type="button" onMouseDown={commitTyped}
          style={{ marginTop: 6, background: 'none', border: `1px solid ${B.border}`,
                   borderRadius: 6, padding: '5px 9px', cursor: 'pointer',
                   fontSize: 12, color: B.navy, fontFamily: 'inherit' }}>
          Use code {typedCode}
        </button>
      )}
      {!shapeError && !searchErr && !typedCode && (
        <div style={{ fontSize: 12, color: B.textLt, marginTop: 4 }}>
          {hint ?? 'Optional — left blank, it is derived from the deal’s documents.'}
        </div>
      )}

      {open && !searchErr && results.length === 0 && term.trim().length > 1 && !typedCode && (
        <div style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%', marginTop: 4,
                      background: '#fff', border: `1px solid ${B.border}`, borderRadius: 8,
                      padding: '9px 11px', fontSize: 13, color: B.textLt,
                      boxShadow: '0 6px 18px rgba(0,0,0,0.10)' }}>
          No industry matches “{term.trim()}”.
        </div>
      )}

      {open && results.length > 0 && (
        <ul style={{
          position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%', marginTop: 4,
          maxHeight: 280, overflowY: 'auto', listStyle: 'none', padding: 4, margin: 0,
          background: '#fff', border: `1px solid ${B.border}`, borderRadius: 8,
          boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
        }}>
          {results.map(r => (
            <li key={r.code}>
              <button type="button" onMouseDown={() => pick(r)}
                style={{
                  display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '7px 9px',
                  background: 'none', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = B.bgAlt || '#f5f5f5'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                <span style={{ fontWeight: 600, minWidth: 56 }}>{r.code}</span>
                <span style={{ color: B.text }}>{r.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
